import React from "react";
import { footerPropType } from "../../propTypes";
import MoleculeEmailCapture from "components/source/molecules/molecule-email-capture";
import AppIcon from "images/design-system-svgs/footer/app.svg";
import InstagramIcon from "images/design-system-svgs/footer/instagram.svg";
import TikTokIcon from "images/design-system-svgs/footer/tiktok.svg";
import FacebookIcon from "images/design-system-svgs/footer/facebook.svg";
import PinterestIcon from "images/design-system-svgs/footer/pinterest.svg";
import SubStackIcon from "images/design-system-svgs/footer/substack.svg";
import RAFIcon from "images/design-system-svgs/footer/RAF.svg";
import CreatorIcon from "images/design-system-svgs/footer/creator.svg";
import FooterRTR from "images/design-system-svgs/footer/Footer-RTR.svg";

function Footer({ footer = {} }) {
  const renderLinkSection = (section, i) => {
    return (
      <div data-test-id={`link-section-${i}`} key={section.sectionHeaderCopy}>
        <p className="font-eyebrow mb-2">{section.sectionHeaderCopy}</p>
        <ul>
          {section.links?.map((link, i) => {
            return (
              <li className="py-1" key={i}>
                <a href={link.href}>{link.copy}</a>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderLegalLinks = () => {
    // Classname "ot-sdk-show-settings" is used by OneTrust cookie compliance tool
    return (
      <>
        {footer.legalLinkSection?.map((link, i) => {
          return (
            <a className="block text-left lg:text-center" href={link.href} key={i}>
              {link.copy}
            </a>
          );
        })}
        <div className="text-left lg:text-right">
          <button id="ot-sdk-btn" data-test-id="onetrust-footer-link" className="ot-sdk-show-settings text-right">
            Your Privacy Choices
          </button>
        </div>
      </>
    );
  };

  function renderPromptBody(prompt, Icon, url) {
    return (
      <>
        <div className="flex items-center">
          <Icon className="stroke-ivoryWhite stroke-[1.6] stroke-linecap-round stroke-linejoin-round" />
        </div>

        <div className="grid grid-rows-2">
          <span className="font-eyebrow my-2">{prompt.annotationCopy}</span>

          <a href={url} className="underline text-md">
            {prompt.ctaCopy}
          </a>
        </div>
      </>
    );
  }

  const renderAppDownloadPrompt = () => {
    const { appDownloadPrompt } = footer;

    if (!appDownloadPrompt) {
      return;
    }

    return renderPromptBody(appDownloadPrompt, AppIcon, "https://rtr.app.link/dsabSPp3GB");
  };

  const renderReferralPrompt = () => {
    const { appReferralPrompt } = footer;

    if (!appReferralPrompt) {
      return;
    }

    return renderPromptBody(
      appReferralPrompt,
      RAFIcon,
      "https://www.renttherunway.com/invite_a_friend?action_type=footer_link"
    );
  };

  const renderCreatorPrompt = () => {
    const { appCreatorPrompt } = footer;

    if (!appCreatorPrompt) {
      return;
    }

    return renderPromptBody(appCreatorPrompt, CreatorIcon, "https://www.renttherunway.com/content-pages/RTRMuse");
  };

  const renderSocialIcons = () => {
    const { socialLinkSection, socialLinkSection: { activeSocialMediaLinks } = {} } = footer;

    return (
      <div className="grid grid-rows-2">
        <span className="font-eyebrow my-2">{socialLinkSection?.sectionHeaderCopy}</span>

        <div className="grid grid-cols-5">
          {activeSocialMediaLinks?.includes("INSTAGRAM") && (
            <a href="http://www.instagram.com/renttherunway" aria-label="instagram">
              <InstagramIcon className="text-ivoryWhite" />
            </a>
          )}
          {activeSocialMediaLinks?.includes("TIKTOK") && (
            <a href="https://www.tiktok.com/@renttherunway" aria-label="tiktok">
              <TikTokIcon className="text-ivoryWhite" />
            </a>
          )}
          {activeSocialMediaLinks?.includes("FACEBOOK") && (
            <a href="https://www.facebook.com/RentTheRunway" aria-label="facebook">
              <FacebookIcon className="text-ivoryWhite" />
            </a>
          )}
          {activeSocialMediaLinks?.includes("PINTEREST") && (
            <a href="http://www.pinterest.com/renttherunway/" aria-label="pinterest">
              <PinterestIcon className="text-ivoryWhite" />
            </a>
          )}
          {activeSocialMediaLinks?.includes("TWITTER") && (
            <a href="https://renttherunway.substack.com/" aria-label="substack">
              <SubStackIcon />
            </a>
          )}
        </div>
      </div>
    );
  };

  const renderSocialSection = () => {
    return (
      <>
        <div className="grid grid-rows-2 h-full w-full sm:w-footerBlock">
          <div className="grid grid-cols-[1fr_4fr] gap-4 sm:items-start mb-8 sm:mb-0">{renderAppDownloadPrompt()}</div>
          <div className="grid grid-cols-[1fr_4fr] gap-4 sm:items-end mb-8 lg:mb-0">{renderReferralPrompt()}</div>
        </div>
        <div className="grid grid-rows-2 h-full w-full sm:w-footerBlock">
          <div className="grid grid-cols-[1fr_4fr] gap-4 sm:items-start mb-8 lg:mb-0">{renderCreatorPrompt()}</div>
          <div className="grid gap-4 lg:items-end mb-8 sm:mb-0">{renderSocialIcons()}</div>
        </div>
      </>
    );
  };

  const renderFooterBottom = () => {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 sm:gap-4">
        <p className="col-span-2 sm:col-span-1">©{new Date().getFullYear()} Rent the Runway. All Rights Reserved.</p>
        {renderLegalLinks()}
      </div>
    );
  };

  const render = () => {
    return (
      <>
        <MoleculeEmailCapture copy={footer.emailCaptureForm} />
        <div id="footer" className="flex justify-center bg-darkBurgundy text-ivoryWhite text-sm">
          <div className="flex flex-col w-full px-6 py-20 sm:px-32 sm:pt-24 sm:pb-12">
            <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-9`}>
              <>{footer?.linkSections?.map((ls, i) => renderLinkSection(ls, i))}</>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-footerIconGrid sm:gap-4 w-full sm:mb-12">
              <div className="self-start mb-8 lg:mb-0">
                <FooterRTR className="text-ivoryWhite w-full h-auto max-w-xl" />
              </div>
              {renderSocialSection()}
            </div>

            {renderFooterBottom()}
          </div>
        </div>
      </>
    );
  };

  return render();
}

Footer.propTypes = footerPropType;

export default Footer;
